import { TransactionStatus } from 'graphql/data/__generated__/types-and-hooks'

import { Activity, ActivityMap } from './types'

/* Detects transactions from same account with the same nonce and different hash */
function wasTxCancelled(localActivity: Activity, remoteMap: ActivityMap, account: string): boolean {
  // handles locally cached tx's that were stored before we started tracking nonces
  if (!localActivity.nonce || localActivity.status !== TransactionStatus.Pending) return false

  return Object.values(remoteMap).some((remoteTx) => {
    if (!remoteTx) return false

    // Cancellations are only possible when both nonce and tx.from are the same
    if (remoteTx.nonce === localActivity.nonce && remoteTx.receipt?.from.toLowerCase() === account.toLowerCase()) {
      // If the remote tx has a different hash than the local tx, the local tx was cancelled
      return remoteTx.hash.toLowerCase() !== localActivity.hash.toLowerCase()
    }
    return false
  })
}

function containsUnknown(value?: string): boolean {
  return /\bunknown\b/i.test(value ?? '')
}

/**
 * Receipt data from the backend is authoritative, but its transaction classification can lag behind the local record.
 * Keep locally-recorded labels when the remote activity is unclassified or explicitly contains unknown metadata.
 */
export function combineActivities(
  localMap: ActivityMap = {},
  remoteMap: ActivityMap = {},
  account: string
): Activity[] {
  const txHashes = [...new Set([...Object.keys(localMap), ...Object.keys(remoteMap)])]

  return txHashes.reduce((activities: Activity[], hash) => {
    const localActivity = localMap[hash]
    const remoteActivity = remoteMap[hash]

    if (localActivity && wasTxCancelled(localActivity, remoteMap, account)) return activities
    if (!localActivity && remoteActivity) {
      activities.push(remoteActivity)
      return activities
    }
    if (localActivity && !remoteActivity) {
      activities.push(localActivity)
      return activities
    }
    if (!localActivity || !remoteActivity) return activities

    const preferLocalClassification = remoteActivity.isRemoteUnclassified || containsUnknown(remoteActivity.title)
    const preferLocalDescriptor = preferLocalClassification || containsUnknown(remoteActivity.descriptor)

    activities.push({
      ...localActivity,
      ...remoteActivity,
      title: preferLocalClassification ? localActivity.title : remoteActivity.title,
      descriptor: preferLocalDescriptor
        ? localActivity.descriptor ?? remoteActivity.descriptor
        : remoteActivity.descriptor,
      currencies:
        preferLocalClassification || !remoteActivity.currencies?.length
          ? localActivity.currencies ?? remoteActivity.currencies
          : remoteActivity.currencies,
      logos:
        preferLocalClassification || !remoteActivity.logos?.length
          ? localActivity.logos ?? remoteActivity.logos
          : remoteActivity.logos,
      otherAccount:
        preferLocalClassification || !remoteActivity.otherAccount
          ? localActivity.otherAccount ?? remoteActivity.otherAccount
          : remoteActivity.otherAccount,
    })

    return activities
  }, [])
}
